import React, { useState } from "react";
import { Modal, Button, Input, message } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const WidgetModal = ({ visible, onClose, formId, formTitle }) => {
  const [copied, setCopied] = useState(false);

  const baseUrl = window.location.origin;

  const generateEmbedCode = () => {
    return `<script
  src="${baseUrl}/widget.js"
  data-form-id="${formId}"
  async>
</script>`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopied(true);
      message.success("Embedding code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error("Failed to copy to clipboard");
    }
  };

  const instructions = [
    {
      title: "Step 1: Copy the Embedding Code",
      content:
        "Copy the script tag provided above and paste it into your website's HTML where you want the widget to appear.",
    },
    {
      title: "Step 2: Place in Your HTML",
      content:
        "Add the script tag before the closing </body> tag of your HTML page, or in the <head> section.",
    },
    {
      title: "Step 3: Widget Appearance",
      content:
        "The widget will appear as a floating button with the following default settings:",
      details: [
        "Position: Bottom-right corner of the page",
        "Button: Chat emoji (💬) with gradient background",
        "Behavior: Clicking opens a modal with the form",
      ],
    },
    {
      title: "Step 4: Test Your Implementation",
      content:
        "Once implemented, the floating widget button will appear on your website. Click it to test the form functionality.",
    },
  ];

  return (
    <Modal
      title="Widget Embedding"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div className="space-y-6">
        {/* Embedding Code */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-md font-semibold m-0">Embedding Code</h3>
            <Button
              type="primary"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
              className="flex items-center gap-2"
            >
              {copied ? "Copied!" : "Copy Code"}
            </Button>
          </div>
          <TextArea
            value={generateEmbedCode()}
            readOnly
            rows={6}
            className="font-mono text-sm"
            style={{ backgroundColor: "#f5f5f5" }}
          />
        </div>

        {/* Instructions */}
        <div>
          <h3 className="text-md font-semibold m-0">
            Implementation Instructions
          </h3>
          <div className="space-y-4">
            {instructions.map((instruction, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {instruction.title}
                </h4>
                <p className="text-gray-700 mb-2">{instruction.content}</p>
                {instruction.details && (
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    {instruction.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Preview Info */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Widget Preview</h4>
          <p className="text-blue-800 text-sm">
            The widget will appear as a floating button on your website. When
            clicked, it will open a modal containing the form:{" "}
            <strong>"{formTitle}"</strong>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default WidgetModal;
